import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  ReactRequiredChildren,
  ReactDefaultProps,
  ReactComponentStaticProps,
} from "../../types/react";

import { HeadProvider, HeadContextType, alignmentValues } from "./HeadContext";
import { ComparisonTableHeadColHeaderProps } from "./HeadColHeader";
import { useTable } from "./TableContext";

import styles from "./ComparisonTable.scss";

type ComparisonTableHeadBaseProps = {
  alignment?: HeadContextType["alignment"];
};

export type ComparisonTableHeadProps = ComparisonTableHeadBaseProps &
  Omit<
    ReactRequiredChildren<React.ComponentPropsWithoutRef<"thead">>,
    keyof ComparisonTableHeadBaseProps
  >;

const defaultProps = {
  alignment: "center",
} as const;

const validatedDefaultProps: ReactDefaultProps<ComparisonTableHeadBaseProps> = defaultProps;

export const ComparisonTableHead: ((
  props: ComparisonTableHeadProps
) => JSX.Element) &
  ReactComponentStaticProps<ComparisonTableHeadProps> = ({
  children,
  alignment = defaultProps.alignment,
  ...restProps
}) => {
  const { hasColumnHeader, setHeaderColumnIds } = useTable();
  const ids: string[] = [];

  useEffect(() => {
    for (let i = 0; i < React.Children.count(children); i += 1) {
      ids.push(`head-col-${i}`);
    }
    setHeaderColumnIds(ids);
  }, [children]);

  return (
    <HeadProvider alignment={alignment}>
      <thead {...restProps}>
        <tr>
          {hasColumnHeader && (
            <td id="blank" className={styles["blank-corner-cell"]} />
          )}
          {React.Children.map(children, (child, index) => {
            const childItem = child as React.ReactElement<
              ComparisonTableHeadColHeaderProps
            >;
            return React.cloneElement(childItem, { id: `head-col-${index}` });
          })}
        </tr>
      </thead>
    </HeadProvider>
  );
};

ComparisonTableHead.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  alignment: PropTypes.oneOf(alignmentValues),
};

ComparisonTableHead.defaultProps = validatedDefaultProps;

ComparisonTableHead.displayName = "ComparisonTable.Head";

export default ComparisonTableHead;
